import React from "react"
import { motion } from "framer-motion"
import { Briefcase, Target, GraduationCap, FileText, Mic, Users, CheckCircle, ArrowRight, BrainCircuit } from "lucide-react"
import Layout from "../../components/common/Layout"
import SEO from "../../components/common/SEO"
import "./ServicePage.css"

const CareerCounseling = () => {
  const processSteps = [
    { num: 1, title: "Assessment", desc: "Scientific evaluation of interests, skills, and personality traits." },
    { num: 2, title: "Exploration", desc: "Discover diverse career options and global educational pathways." },
    { num: 3, title: "Planning", desc: "Create a personalized roadmap to achieve your long-term goals." },
    { num: 4, title: "Implementation", desc: "Ongoing mentorship and support as you embark on your journey." }
  ]

  const serviceOffers = [
    { icon: <Target className="text-gradient" />, title: "Career Assessment", desc: "Identify your strengths through expert testing." },
    { icon: <GraduationCap className="text-gradient" />, title: "Course Selection", desc: "Align your program with your career vision." },
    { icon: <BrainCircuit className="text-gradient" />, title: "Psychometric Testing", desc: "Get deep insights into your core potential." },
    { icon: <FileText className="text-gradient" />, title: "Resume Building", desc: "Stand out with a professional global profile." },
    { icon: <Mic className="text-gradient" />, title: "Interview Prep", desc: "Master interviews with expert-led mock sessions." },
    { icon: <Users className="text-gradient" />, title: "Industry Sync", desc: "Connect with global mentors and professionals." }
  ]

  return (
    <Layout>
      <SEO
        title="Career Counseling - Professional Career Guidance | Welsh Education Group"
        description="Expert career counseling for students to find their perfect path."
        url="/services/career"
      />

      <div className="service-page">
        <section className="service-hero-modern">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="hero-icon-large"
            >
              <Briefcase size={40} />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Career Counseling
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Designing your future with data-driven insights and professional mentorship. We help you find your true calling.
            </motion.p>
          </div>
        </section>

        <section className="service-content-modern">
          <div className="container">
            <div className="service-grid-modern">
              <div className="main-service-info">
                <h2>Design Your Success</h2>
                <p>
                  In a rapidly evolving global job market, choosing the right career path is more critical than ever. Our certified counselors use psychometric tools and industry knowledge to provide you with a clear, actionable career strategy.
                </p>

                <div className="service-features-list">
                  {serviceOffers.map((offer, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="feature-pill"
                    >
                      <div className="feature-pill-icon">{offer.icon}</div>
                      <div className="feature-pill-content">
                        <h4>{offer.title}</h4>
                        <p>{offer.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="process-timeline" style={{ marginTop: '80px' }}>
                  <h2 style={{ marginBottom: '40px' }}>Our 4-Step Strategy</h2>
                  <div className="timeline-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
                    {processSteps.map((step, idx) => (
                      <div key={idx} className="info-card" style={{ borderTop: '4px solid var(--purple-glow)' }}>
                        <h1 style={{ opacity: 0.1, fontSize: '48px', marginBottom: '0' }}>0{step.num}</h1>
                        <h3 style={{ marginTop: '-10px' }}>{step.title}</h3>
                        <p style={{ fontSize: '14px', color: 'var(--muted-foreground)' }}>{step.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="service-sidebar-modern">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  className="sidebar-cta-card"
                >
                  <Users className="text-gradient" size={48} style={{ margin: '0 auto 24px' }} />
                  <h3>Book Expert Session</h3>
                  <p>Speak with a certified psychologist and career mentor.</p>
                  <button className="sidebar-btn highlight-glow">Schedule Now</button>
                </motion.div>

                <div className="info-card">
                  <h3 style={{ fontSize: '18px', marginBottom: '20px' }}>Success Story</h3>
                  <p style={{ fontStyle: 'italic', marginBottom: '16px' }}>"The psychometric testing revealed strengths I never knew I had. Now I'm thriving in my Master's at a top university!"</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '40px', height: '40px', background: 'var(--card-border)', borderRadius: '50%' }}></div>
                    <div>
                      <h5 style={{ margin: 0 }}>Aditi Sharma</h5>
                      <span style={{ fontSize: '12px', color: 'var(--cyan-glow)' }}>Masters Student, UK</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  )
}

export default CareerCounseling
